import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Building2 } from "@fleet/icons";
import { Badge, Card, CardContent, Skeleton } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import type { DirectoryUser, Membership } from "./user-detail-view";

/**
 * F2-C — roles & memberships tab. Read-only: the platform role plus the user's
 * organization memberships (org + campus + role) from the `member` table.
 */
export function MembershipsTab(props: {
  user: DirectoryUser;
  memberships: Membership[];
  loading: boolean;
}) {
  const { t } = useI18n();

  return (
    <div class="mt-4 space-y-4">
      <Card>
        <CardContent class="p-5 flex items-center justify-between">
          <div>
            <div class="text-sm text-muted-foreground">{t(msg`Platform role`)}</div>
            <div class="text-xs text-muted-foreground">
              {t(msg`Back-office role. Change it in the Danger zone.`)}
            </div>
          </div>
          <Badge variant="secondary" class="capitalize">
            {props.user.role}
          </Badge>
        </CardContent>
      </Card>

      <div>
        <h3 class="text-sm font-semibold text-muted-foreground mb-2">
          {t(msg`Organization memberships`)}
        </h3>
        <Show when={!props.loading} fallback={<Skeleton class="h-16 w-full rounded-xl" />}>
          <div class="space-y-2">
            <For
              each={props.memberships}
              fallback={
                <Card>
                  <CardContent class="p-6 text-center text-sm text-muted-foreground">
                    {t(msg`Not a member of any organization.`)}
                  </CardContent>
                </Card>
              }
            >
              {(m) => (
                <Card>
                  <CardContent class="p-4 flex items-center justify-between gap-3">
                    <div class="min-w-0 flex items-center gap-3">
                      <Building2 class="h-5 w-5 text-muted-foreground shrink-0" />
                      <div class="min-w-0">
                        <div class="font-medium truncate">{m.organizationName}</div>
                        <Show when={m.campusName}>
                          <div class="text-xs text-muted-foreground truncate">
                            {t(msg`Campus:`)} {m.campusName}
                          </div>
                        </Show>
                      </div>
                    </div>
                    <Badge variant="outline" class="capitalize shrink-0">
                      {m.role}
                    </Badge>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>
        </Show>
      </div>
    </div>
  );
}
